/**
 * Admin Trip Templates Service
 *
 * Callable wrappers for admin-only template operations. Used when TemplateBuilder
 * is in admin mode (isAdminMode=true).
 */

import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/functions';
import { clearTemplatesCache } from '@/services/tripTemplates';
import type { TripTemplate, TemplateDay } from '@/types/tripTemplates';
import type { TripDestination } from '@/services/trips';

export type AdminSaveTemplateOptions = {
  /** When false, saves as draft (not visible in marketplace). Default true. */
  publish?: boolean;
};

/** Payload shape accepted by the adminCreateTripTemplate Cloud Function. */
export type AdminCreateTripTemplatePayload = {
  title: string;
  destination: string;
  duration: number;
  shortDescription: string;
  longDescription?: string;
  coverImage?: string;
  coverImageCredit?: string;
  coverImageCreditUrl?: string;
  difficulty: TripTemplate['difficulty'];
  travelStyle: string[];
  estimatedBudget: TripTemplate['estimatedBudget'];
  itinerary: TemplateDay[];
  bestMonths?: string[];
  tags?: string[];
  highlights?: string[];
  featured?: boolean;
  publish?: boolean;
  destinations?: Array<Omit<TripDestination, 'startDate' | 'endDate'> & { startDate: string; endDate: string }>;
};

/** Draft-like object from TemplateBuilder (has all template fields except id/createdAt/updatedAt/creator fields). */
export type TemplateDraftForAdmin = {
  title: string;
  destination: string;
  duration: number;
  shortDescription: string;
  longDescription?: string;
  coverImage?: string;
  coverImageCredit?: string;
  coverImageCreditUrl?: string;
  difficulty: TripTemplate['difficulty'];
  travelStyle: string[];
  estimatedBudget: TripTemplate['estimatedBudget'];
  itinerary: TemplateDay[];
  bestMonths?: string[];
  tags?: string[];
  highlights?: string[];
  destinations?: TripDestination[];
};

/**
 * Serialize template draft for the callable (dates to ISO strings).
 */
function serializeForCallable(
  draft: TemplateDraftForAdmin,
  options?: AdminSaveTemplateOptions,
): AdminCreateTripTemplatePayload {
  const publish = options?.publish !== false;
  const payload: AdminCreateTripTemplatePayload = {
    title: draft.title,
    destination: draft.destination,
    duration: draft.duration,
    shortDescription: draft.shortDescription,
    longDescription: draft.longDescription,
    coverImage: draft.coverImage,
    coverImageCredit: draft.coverImageCredit,
    coverImageCreditUrl: draft.coverImageCreditUrl,
    difficulty: draft.difficulty,
    travelStyle: draft.travelStyle,
    estimatedBudget: draft.estimatedBudget,
    itinerary: draft.itinerary,
    bestMonths: draft.bestMonths ?? [],
    tags: draft.tags ?? [],
    highlights: draft.highlights ?? [],
    featured: publish,
    publish,
  };
  if (draft.destinations && draft.destinations.length > 0) {
    payload.destinations = draft.destinations.map((d) => ({
      ...d,
      startDate: (d.startDate instanceof Date ? d.startDate : new Date(d.startDate)).toISOString(),
      endDate: (d.endDate instanceof Date ? d.endDate : new Date(d.endDate)).toISOString(),
    }));
  }
  return payload;
}

/**
 * Create a curated trip template via Cloud Function. Admin only.
 */
export async function adminCreateTripTemplate(
  templateData: TemplateDraftForAdmin,
  options?: AdminSaveTemplateOptions,
): Promise<string> {
  const callable = httpsCallable<AdminCreateTripTemplatePayload, { templateId: string }>(
    functions,
    'adminCreateTripTemplate'
  );
  const payload = serializeForCallable(templateData, options);
  const result = await callable(payload);
  const data = result.data;
  if (!data?.templateId) {
    throw new Error('Empty response from adminCreateTripTemplate');
  }
  await clearTemplatesCache();
  return data.templateId;
}

/**
 * Update an existing trip template via Cloud Function. Admin only.
 */
export async function adminUpdateTripTemplate(
  templateId: string,
  templateData: TemplateDraftForAdmin,
  options?: AdminSaveTemplateOptions,
): Promise<string> {
  const callable = httpsCallable<
    AdminCreateTripTemplatePayload & { templateId: string },
    { templateId: string }
  >(functions, 'adminUpdateTripTemplate');
  const payload = { ...serializeForCallable(templateData, options), templateId };
  const result = await callable(payload);
  const data = result.data;
  if (!data?.templateId) {
    throw new Error('Empty response from adminUpdateTripTemplate');
  }
  await clearTemplatesCache();
  return data.templateId;
}

/**
 * Delete a trip template via Cloud Function. Admin only.
 */
export async function adminDeleteTripTemplate(templateId: string): Promise<void> {
  const callable = httpsCallable<{ templateId: string }, void>(
    functions,
    'adminDeleteTripTemplate'
  );
  await callable({ templateId });
}

/**
 * Approve a community-submitted trip template. Admin only.
 */
export async function adminApproveTripTemplate(
  templateId: string,
  moderationNotes?: string
): Promise<void> {
  const callable = httpsCallable<
    { templateId: string; moderationNotes?: string },
    { templateId: string }
  >(functions, 'adminApproveTripTemplate');
  await callable({ templateId, moderationNotes });
}

/**
 * Reject a community-submitted trip template. Admin only.
 * rejectionReason is required (shown to creator); moderationNotes is optional (internal).
 */
export async function adminRejectTripTemplate(
  templateId: string,
  rejectionReason: string,
  moderationNotes?: string
): Promise<void> {
  const callable = httpsCallable<
    { templateId: string; rejectionReason: string; moderationNotes?: string },
    { templateId: string }
  >(functions, 'adminRejectTripTemplate');
  await callable({ templateId, rejectionReason, moderationNotes });
}
